import { useEffect, useMemo, useState } from "react";

export type ColumnFilterConfig<T> = {
  key: string;
  accessor: (row: T) => string;
};

// Stores **deselected** values per column.
// A missing key means "nothing deselected" (all selected).
// A key with a Set means those specific values are excluded.
export type ColumnFilterState = Record<string, Set<string>>;

const STORAGE_PREFIX = "column_filters_v6:";

function loadPersistedFilters(persistKey: string): ColumnFilterState {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_PREFIX + persistKey) : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string[]>;
    const state: ColumnFilterState = {};
    for (const [col, values] of Object.entries(parsed)) {
      if (Array.isArray(values) && values.length > 0) state[col] = new Set(values);
    }
    return state;
  } catch {
    return {};
  }
}

function savePersistedFilters(persistKey: string, filters: ColumnFilterState) {
  try {
    const serialized: Record<string, string[]> = {};
    for (const [col, set] of Object.entries(filters)) {
      if (set && set.size > 0) serialized[col] = Array.from(set);
    }
    localStorage.setItem(STORAGE_PREFIX + persistKey, JSON.stringify(serialized));
  } catch {
    // ignore
  }
}

function getCleanedDeselections(
  deselected: Set<string> | undefined,
  currentValues: string[]
): Set<string> | null {
  if (!deselected || deselected.size === 0) return null;
  const allowedValues = new Set(currentValues);
  const cleaned = new Set<string>();
  deselected.forEach((value) => {
    if (allowedValues.has(value)) cleaned.add(value);
  });
  return cleaned;
}

export type UseColumnFiltersOptions = { persistKey?: string };

export function useColumnFilters<T>(
  rows: T[],
  configs: ColumnFilterConfig<T>[],
  options?: UseColumnFiltersOptions
) {
  const persistKey = options?.persistKey ?? undefined;
  const [filters, setFilters] = useState<ColumnFilterState>(() =>
    persistKey ? loadPersistedFilters(persistKey) : {}
  );

  const accessorMap = useMemo(() => {
    const map = new Map<string, (row: T) => string>();
    configs.forEach(cfg => map.set(cfg.key, cfg.accessor));
    return map;
  }, [configs]);

  // filters now stores deselected values: exclude rows whose value IS in the set
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      for (const [key, deselected] of Object.entries(filters)) {
        if (!deselected || deselected.size === 0) continue;
        const accessor = accessorMap.get(key);
        if (!accessor) continue;
        const raw = (accessor(row) ?? "") as string;
        const value = typeof raw === "string" ? raw.trim() : String(raw);
        if (deselected.has(value) || deselected.has(raw)) return false;
      }
      return true;
    });
  }, [rows, filters, accessorMap]);

  useEffect(() => {
    if (persistKey) savePersistedFilters(persistKey, filters);
  }, [persistKey, filters]);

  const uniqueValues = useMemo(() => {
    const map: Record<string, string[]> = {};
    configs.forEach(cfg => {
      const values = new Set<string>();
      rows.forEach(row => {
        const val = cfg.accessor(row) ?? "";
        values.add(val);
      });
      map[cfg.key] = Array.from(values).sort((a, b) => a.localeCompare(b));
    });
    return map;
  }, [rows, configs]);

  // Prune stale deselected values that no longer exist in the current dataset.
  // Skip entirely when rows haven't loaded yet — an empty dataset would incorrectly
  // wipe every persisted filter value before data arrives, breaking persistence on refresh.
  useEffect(() => {
    if (rows.length === 0) return;

    setFilters((prev) => {
      let changed = false;
      const next: ColumnFilterState = { ...prev };

      configs.forEach((cfg) => {
        const column = cfg.key;
        const deselected = prev[column];
        const cleaned = getCleanedDeselections(deselected, uniqueValues[column] ?? []);
        if (!deselected || !cleaned) return;

        if (cleaned.size !== deselected.size) {
          if (cleaned.size === 0) {
            delete next[column];
          } else {
            next[column] = cleaned;
          }
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [rows.length, configs, uniqueValues]);

  function setFilterValue(column: string, value: string, checked: boolean) {
    setFilters(prev => {
      const next: ColumnFilterState = { ...prev };
      const deselected = prev[column] ? new Set(prev[column]) : new Set<string>();

      if (checked) {
        deselected.delete(value);
      } else {
        deselected.add(value);
      }

      if (deselected.size === 0) {
        delete next[column];
      } else {
        next[column] = deselected;
      }
      return next;
    });
  }

  /** Set the filter so only `selectedValues` are shown; everything else is deselected. */
  function selectOnly(column: string, selectedValues: string[]) {
    setFilters(prev => {
      const next: ColumnFilterState = { ...prev };
      const allValues = uniqueValues[column] ?? [];
      const selectedSet = new Set(selectedValues);
      const deselected = new Set(allValues.filter(v => !selectedSet.has(v)));
      if (deselected.size === 0) {
        delete next[column];
      } else {
        next[column] = deselected;
      }
      return next;
    });
  }

  function clearFilter(column: string) {
    setFilters(prev => {
      if (!(column in prev)) return prev;
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }

  function clearAllFilters() {
    setFilters({});
  }

  function isActive(column: string) {
    return !!filters[column] && filters[column]!.size > 0;
  }

  return {
    filteredRows,
    uniqueValues,
    filters,
    setFilterValue,
    selectOnly,
    clearFilter,
    clearAllFilters,
    isActive,
  };
}
